/**
 * Review tab — the v1 review page body, unchanged.
 *
 * Everything here was lifted verbatim out of `createViewFileReview` when FIN-605
 * turned that component into the v2 shell: the file-metadata grid, the validation
 * and lock banners, `ValidationOutcomePanel`, the line-items table with its action
 * toolbar, and every modal. The shell owns the file load, so this component
 * receives an already-loaded file and never renders a loading or error state.
 */
import type * as React from "react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { Download, RefreshCw, FileText, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { Loader, Button, Modal, Input, Label } from "@/ui/atoms";
import { DataTable, ScrollableRow, WarningsPanel } from "@/ui/molecules";
import { MultiSelect } from "@/ui/molecules/MultiSelect";
import { SearchableSelect } from "@/ui/molecules/SearchableSelect";
import type { TSearchableOption } from "@/ui/molecules/SearchableSelect";
import { cn } from "@/utils/utils";
import type { TApiFileStatus, TApiLineItemQuery } from "@/apis/proda_finance/types";
import type { ColumnFiltersState, ExpandedState, SortingState } from "@tanstack/react-table";
import { useServerTable } from "@/useCases/useServerTable";
import {
    lineItemColumnFiltersToQuery,
    lineItemFacetQuery,
    withSelectedFacetValues,
    hasAnyLineItemColumnFilter,
} from "@/modules/rev_eu/review/views/ViewGLLines/lineItemQuery";
import { ValidationOutcomePanel } from "./ValidationOutcomePanel";
import type { TFileReviewDetails, TReviewLineItem } from "./types";
import type { TReviewTabUseCases } from "./useCases";
import { createLineItemColumns } from "./lineItemColumns";
import { SplitCountModal } from "./SplitCountModal";
import { PROPERTY_LINKED_SEGMENT, PROPERTY_SCOPED_SEGMENT } from "./lineItemFields";
import { formatCodeLabel } from "@/utils/formatCodeLabel";
import { formatDateTimeEuropeBerlin } from "@/utils/europeBerlinTime";
import { useLineItemEditor } from "./useLineItemEditor";
import { useAutoPopulate } from "./useAutoPopulate";
import { canDownloadFileArtifact } from "./canDownloadFileArtifact";

/** Strip the ERROR_CODE prefix (e.g. "INACTIVE_PROPERTY_CODE: ...") from a validation error. */
function stripErrorCode(error: string): string {
    const match = error.match(/^[A-Z][A-Z0-9_]+:\s*(.+)$/);
    return match?.[1] ?? error;
}

/** Normalize validation errors from backend (string or string[]). */
/**
 * Flatten the API's `string[] | string | null` message shape into a list.
 *
 * The backend stores these columns as a JSON array in a single text column, so a
 * plain string may itself be JSON that needs decoding before display.
 */
function parseMessageList(messages: string[] | string | null): string[] {
    if (!messages) return [];

    const parseMessage = (value: unknown): string[] => {
        if (typeof value !== "string") return [];

        try {
            const parsed = JSON.parse(value) as unknown;

            if (Array.isArray(parsed)) {
                return parsed.filter((x): x is string => typeof x === "string");
            }

            return [value];
        } catch {
            return [value];
        }
    };

    return Array.isArray(messages) ? messages.flatMap(parseMessage) : parseMessage(messages);
}

function validationErrorsToText(validationErrors: string[] | string | null): string[] {
    return parseMessageList(validationErrors).map(stripErrorCode);
}

/** Sub-rows for the review grid: a line's split rows, when it has any. */
const getSplitSubRows = (row: TReviewLineItem): TReviewLineItem[] | undefined =>
    row.splitChildren.length > 0 ? row.splitChildren : undefined;

/**
 * Highlight split groups: a split original in amber, its split rows in a lighter
 * tint. This is what makes a split visible at a glance in a long grid.
 */
const splitRowClassName = (row: TReviewLineItem, depth: number): string | undefined => {
    if (depth > 0 || row.isSplitChild) {
        return "bg-amber-50/40 even:bg-amber-50/40 hover:bg-amber-100/50";
    }
    if (row.splitChildren.length > 0) {
        return "bg-amber-50 even:bg-amber-50 hover:bg-amber-100/70";
    }
    return undefined;
};

/**
 * Notice shown inside an expanded split, above its rows.
 *
 * Only rendered while the split does not add up: once the amounts are non-zero and
 * sum to the original, the group is valid and the surrounding actions re-enable, so
 * the warning would be stale.
 */
const renderSplitBanner = (row: TReviewLineItem): React.ReactNode => {
    if (row.splitChildren.length === 0) return null;
    const total = row.splitChildren.reduce((sum, child) => sum + Number(child.amount || 0), 0);
    const originalAmount = Number(row.amount || 0);
    const hasZeroAmount = row.splitChildren.some((child) => Number(child.amount || 0) === 0);
    if (!hasZeroAmount && total === originalAmount) return null;
    return (
        <div
            className="border-l-4 border-destructive bg-destructive/10 px-4 py-2 text-sm text-destructive"
            role="alert"
            data-testid="split-incomplete-notice"
        >
            Actions outside of this area will be disabled if and until the sum of the amounts of the
            split rows do not add up to the original row&apos;s amount.
        </div>
    );
};

const baseDownloadTypes = [
    { value: "original", label: "Original File" },
    { value: "processed", label: "Processed File" },
];

function getStatusOptions(
    status: string,
    fileType: string,
    completion: { canMarkCompleted: boolean; completedFromStatus: string | null },
): { value: string; label: string }[] {
    const isOTB = fileType === "open_trial_balance";
    const current = { value: status, label: formatStatus(status) };

    // Product-Owner-only: close an exported period, or reopen a closed one back to
    // the status it was closed from.
    if (completion.canMarkCompleted) {
        if (status === "finjournal_generated" || status === "data_platform_generated") {
            return [current, { value: "completed", label: "Completed" }];
        }
        // A "balanced" reopen target is by definition a cascaded TB_AND_GL trial
        // balance — it must be reopened via its GL sibling, which the backend
        // enforces with FILE_REOPEN_GL_FIRST. Don't offer the option here.
        if (
            status === "completed" &&
            completion.completedFromStatus &&
            completion.completedFromStatus !== "balanced"
        ) {
            return [
                current,
                {
                    value: completion.completedFromStatus,
                    label: formatStatus(completion.completedFromStatus),
                },
            ];
        }
    }

    if (status === "under_review") {
        const target = isOTB
            ? { value: "ready_for_commitment", label: "Ready for Commitment" }
            : { value: "ready_for_balancing", label: "Ready for Balancing" };
        return [current, target];
    }
    if (status === "ready_for_balancing" && !isOTB) {
        return [current, { value: "under_review", label: "Under Review" }];
    }
    if (status === "ready_for_commitment" && isOTB) {
        return [current, { value: "under_review", label: "Under Review" }];
    }
    return [current];
}

function formatStatus(status: string): string {
    return status
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

const EMPTY_LINE_ITEMS: TReviewLineItem[] = [];

// Reader-facing names for the background whole-file operations. Both sources the
// lock banner reads carry the raw FileOperationName value -- `file.lockReason`, which
// the server sets to `name.value`, and `operationStatus.operation` -- so without this
// the banner reads "File is locked (remove_generated_lines in progress)". The older
// lock reasons in the app ("Committed", "Revalidating", "AutoPopulatePropcodes") are
// already written as prose, and anything unrecognised falls through unchanged so those
// keep rendering as before.
const OPERATION_LABELS: Record<string, string> = {
    auto_balance: "Auto-balance",
    generate_gl: "GL generation",
    run_adjustment: "Adjustment",
    remove_generated_lines: "Removal of generated lines",
    reprocess: "Refresh",
    revalidate: "Revalidation",
};

// Open on Trannum ascending so a transaction's rows sit together, matching the order the
// FinJournal export writes them in. Reviewers check that each Trannum balances, which the
// uploaded file's own row order scatters. Sorting stays user-changeable from the headers.
const FILE_REVIEW_INITIAL_SORT: SortingState = [{ id: "trannum", desc: false }];

// A file with invalid rows opens on the Is Valid column instead, which puts the error rows
// first ("invalid" sorts ahead of "valid") so the reviewer sees what blocks the commit.
const FILE_REVIEW_ERRORS_FIRST_SORT: SortingState = [{ id: "isValid", desc: false }];

/**
 * Why every action is disabled on an archived file. One constant so the copy cannot
 * drift between buttons, and deliberately without a "reopen it" hint: unlike a
 * completed file, an archived one has no unarchive action — it comes back only when
 * its deal is reactivated.
 */
export const ARCHIVED_ACTION_TITLE = "File is archived — archived files are read-only";
export type TReviewTabProps = {
    /** The loaded file. The shell does not render this tab until it has one. */
    file: TFileReviewDetails;
    useCases: TReviewTabUseCases;
    /**
     * Sort to start from — the shell hands back the sort the user had on the file
     * they came from. Omitted on a first visit, which falls back to the default.
     */
    initialSort?: SortingState;
    /**
     * Reports the sort as it changes. The shell remounts this tab on a file switch,
     * so this is what lets one choice survive that reset while page, filters, modals
     * and expanded splits are deliberately dropped.
     */
    onSortingChanged?: (sorting: SortingState) => void;
};

export const createReviewTab = () => {
    return function ReviewTab({ file, useCases, initialSort, onSortingChanged }: TReviewTabProps) {
        const {
            loadFacets,
            fetchLineItems,
            useLineItemsState,
            useLineItemsMeta,
            useFacets,
            useOperationState,
            onRefresh,
            onManualRefresh,
            useIsViewingAsAdmin,
            useFinjournalReviewCaps,
            useTbglReviewCaps,
            useCanAutoBalance,
            useCanForceReject,
            useCanMarkCompleted,
            useCanSplitLines,
            onSetContributors,
            useContributorCandidates,
            onAutoBalance,
            onCommitOTB,
            onGenerateGL,
            onManualReject,
            onAutoPopulatePropcodes,
            onAutoPopulateSegments,
            onAutoPopulatePropcodesByFilter,
            onAutoPopulateSegmentsByFilter,
            loadDealSegmentMappings,
            searchSegmentValues,
            getSegmentAllowedValuesBatch,
            onRemoveGeneratedLines,
            onRunAdjustment,
            onGenerateFinJournal,
            onGenerateDataPlatform,
            useSftpSubmissions,
            onSendToReplica,
            onDownload,
            onChangeStatus,
            onEditLineItem,
            onSplitLineItem,
            onReverseSplit,
            onDeleteSplitRow,
            searchMasterPropertyCodes,
            searchYardiAccountsByCode,
            searchYardiAccountsByName,
        } = useCases;

        const lineItemsState = useLineItemsState();
        const lineItemsMeta = useLineItemsMeta();
        const facets = useFacets();
        const operationStatus = useOperationState();
        // Gates the whole-file action buttons AND the review grid's own edit/split
        // controls on the server-derived operation record rather than only the
        // per-button local `isX` flags, so a background operation (reprocess/
        // revalidate can run for many minutes) keeps the grid read-only for its
        // whole duration -- not just while the request that started it is
        // in-flight. Hoisted above `actionsDisabled`/`splitDisabled` (below) so
        // they can fold it in; guards against a stale record from the
        // previously viewed file rendering before this file's own status has
        // loaded.
        const isFileOperationRunning =
            operationStatus?.fileId === file.id && operationStatus?.state === "running";
        const sftpSubmissions = useSftpSubmissions();
        // Newest-first; the latest replica submission drives the delivery banners above
        // the table and the Send-to-Replica button's own state below it.
        const latestReplica = sftpSubmissions.find((s) => s.instance === "replica");
        const isViewingAsAdmin = useIsViewingAsAdmin();
        const finjournalCaps = useFinjournalReviewCaps();
        const tbglCaps = useTbglReviewCaps();
        const canAutoBalance_ = useCanAutoBalance();
        const canForceReject = useCanForceReject();
        const canMarkCompleted = useCanMarkCompleted();
        // A completed file is closed: the Product Owner must reopen it before
        // anything on it can change (mirrors the backend guard).
        const isCompleted = file.status === "completed";
        // An archived file is closed to *everyone*, Product Owner included, and there
        // is no unarchive: it comes back only when its deal is reactivated. Stricter
        // than completed, so it wins the tooltip when both apply.
        const isArchived = !!file.archivedAt;
        // Either freeze blocks a mutation. Deliberately not folded into the download
        // logic below — an archived file's generated output stays downloadable.
        const isFrozen = isCompleted || isArchived;
        // Refresh strategy: the deal's mappings / row-filtering rules changed since
        // this file was parsed, so Refresh has to re-read the source rather than
        // only re-validate. The two counts are the work that rebuild would discard.
        const parseConfigChanged = file.parseConfigChanged;
        const manualEditCount = file.manualEditCount;
        const splitLineCount = file.splitLineCount;
        // Review actions are gated per the permissions matrix by file type. FinJournal
        // files use the FinJournal caps; TB/GL/OTB/TBGL files use the TB/GL caps.
        const isFinJournal = file.fileType === "financial_journals";
        const isTBGL = !isFinJournal;
        // FinJournal write actions are gated purely by permission caps; the backend
        // additionally enforces contributor membership on the write endpoints (any
        // contributor, plus admins, may edit — there is no single-writer lock).
        const finjournalWriteBlocked = isFinJournal && !finjournalCaps.canWrite;
        // Contributor editor: shown only to the uploader/admin on FinJournals. The
        // candidate roster is fetched (role-scoped) only when the editor is enabled.
        const canEditContributors = isFinJournal && file.canEditContributors;
        const contributorCandidates = useContributorCandidates(
            file.dealId || null,
            canEditContributors,
        );
        // Viewer keeps line-edit (canWrite) but not the heavier write verbs (autobalance /
        // autopopulate / generate / adjust / remove), proxied by canAutoBalance_.
        const tbglWriteBlocked = isTBGL && !tbglCaps.canWrite;
        const tbglActionsBlocked = isTBGL && !canAutoBalance_;
        // Line-edit-class controls (edit, manual reject, status transitions) share the
        // backend gate (tbgl_line_edit / finjournal_line_edit) — distinct from the
        // heavier write verbs above. Viewers hold line-edit, so these stay enabled for
        // them, matching what the reject / complete-review / revert endpoints accept.
        const reviewWriteBlocked = finjournalWriteBlocked || tbglWriteBlocked;
        // Refresh (revalidate) is gated by tbgl_refresh / finjournal_line_edit — only
        // Account Manager and Accountant hold these; Viewers, admins and Business
        // Partners do not, so the button is disabled for them.
        const refreshBlocked =
            file.isLocked ||
            (isFinJournal && !finjournalCaps.canRefresh) ||
            (isTBGL && !tbglCaps.canRefresh);

        const [selectedDownloadType, setSelectedDownloadType] = useState("original");
        const [selectedStatus, setSelectedStatus] = useState("");
        const [isAutoBalancing, setIsAutoBalancing] = useState(false);
        const [isCommitting, setIsCommitting] = useState(false);
        const [isRemovingGeneratedLines, setIsRemovingGeneratedLines] = useState(false);
        const [isRunningAdjustment, setIsRunningAdjustment] = useState(false);
        const [isRefreshing, setIsRefreshing] = useState(false);
        const [isReparseConfirmOpen, setIsReparseConfirmOpen] = useState(false);
        // The toolbar renders inside an IIFE and the confirm modal outside it, so
        // the refresh runner is bridged across through a ref.
        const runRefreshRef = useRef<((reparse: boolean) => Promise<void>) | null>(null);
        const [actionNotification, setActionNotification] = useState<{
            type: "success" | "error";
            message: string;
        } | null>(null);
        const [isGeneratingGL, setIsGeneratingGL] = useState(false);
        const [glGenerationError, setGLGenerationError] = useState<string | null>(null);
        const [isGeneratingFinJournal, setIsGeneratingFinJournal] = useState(false);
        const [isSendingToReplica, setIsSendingToReplica] = useState(false);
        const [isFinJournalGenerated, setIsFinJournalGenerated] = useState(false);
        const [isGeneratingDataPlatform, setIsGeneratingDataPlatform] = useState(false);
        const [isDataPlatformGenerated, setIsDataPlatformGenerated] = useState(false);
        const [isSavingContributors, setIsSavingContributors] = useState(false);
        const lineItems = lineItemsState.data ?? EMPTY_LINE_ITEMS;
        // Server-mode: filtered count comes from the API meta total.
        const filteredLineItemCount = lineItemsMeta.total;

        const FILE_REVIEW_PAGE_SIZE = 100;

        const fetchData = useCallback(
            (query: TApiLineItemQuery, signal?: AbortSignal) => {
                void fetchLineItems(file.id, query, signal);
            },
            [fetchLineItems, file.id],
        );

        const toQuery = useCallback(
            (input: {
                columnFilters: ColumnFiltersState;
                sorting: SortingState;
                page: number;
                pageSize: number;
            }): TApiLineItemQuery => ({
                ...lineItemColumnFiltersToQuery(input.columnFilters, input.sorting),
                skip: (input.page - 1) * input.pageSize,
            }),
            [],
        );

        // Fixed at mount: a revalidate that clears the errors must not swap the default under
        // the table (and so get reported below as if the user had picked it).
        const [defaultSort] = useState(() =>
            file.invalidLineItemCount > 0
                ? FILE_REVIEW_ERRORS_FIRST_SORT
                : FILE_REVIEW_INITIAL_SORT,
        );
        const { page, setPage, sorting, onSortingChange, columnFilters, onColumnFiltersChange } =
            useServerTable<TApiLineItemQuery>({
                pageSize: FILE_REVIEW_PAGE_SIZE,
                initialSort: initialSort ?? defaultSort,
                toQuery,
                fetchData,
            });

        useEffect(() => {
            // A default is this file's own, so it is not carried to the next file — that
            // one opens on its own default. Any user-picked sort is a new array and reports.
            if (sorting === defaultSort) return;
            onSortingChanged?.(sorting);
        }, [sorting, defaultSort, onSortingChanged]);

        const activeFilterQuery = useMemo<TApiLineItemQuery>(
            () => lineItemColumnFiltersToQuery(columnFilters, sorting),
            [columnFilters, sorting],
        );

        const hasActiveLineItemFilters = useMemo(
            () => hasAnyLineItemColumnFilter(columnFilters),
            [columnFilters],
        );

        const lineItemRowCount = useMemo(
            () =>
                buildRowCount({
                    total: lineItemsMeta.total,
                    totalUnfiltered: lineItemsMeta.totalUnfiltered,
                    hasActiveFilters: hasActiveLineItemFilters,
                }),
            [lineItemsMeta.total, lineItemsMeta.totalUnfiltered, hasActiveLineItemFilters],
        );

        // `lineItemsMeta` starts at zero, so the count has to stay silent until the
        // first response lands — otherwise the heading reads "0 line items" on a page
        // that is still loading. This is the guard DataTable applies to its own count
        // bar (`showRowCount && !loading`), which did not come across when the count
        // moved onto the heading.
        //
        // `idle` belongs here too: a mounted ReviewTab always fires a fetch from
        // useServerTable's effect below, so `idle` with no payload means the request
        // is imminent, not that the file has no rows. The wire's onUnmount resets
        // lineItemsState to idle/null on every file switch (so a remount cannot
        // paint the previous file's rows), and that reset lands a render before the
        // effect's fetch flips the status to pending — treating idle as "not
        // loading" made that render paint as loaded-and-empty ("No line items found
        // for this file."), which reads as an authoritative statement about the new
        // month rather than the loading state it actually is. A file that genuinely
        // has zero rows still reports correctly, since it reaches `fulfilled` with
        // `data: []`.
        const isLoadingLineItems =
            (lineItemsState.status === "pending" || lineItemsState.status === "idle") &&
            lineItemsState.data === null;

        // Same wording DataTable's own count bar uses, so moving it onto the heading
        // does not quietly change what the number says.
        const lineItemCountLabel = lineItemRowCount.isFiltered
            ? `Showing ${lineItemRowCount.filtered.toLocaleString()} of ${lineItemRowCount.total.toLocaleString()}`
            : `${lineItemRowCount.total.toLocaleString()} ${
                  lineItemRowCount.total === 1 ? "line item" : "line items"
              }`;

        // The column filters work as a group: the facet request carries the whole
        // active set, so every dropdown only offers values that still have matching
        // rows under the other filters. Keyed on the query value (not the filter
        // array identity) and excluding sort/pagination, which cannot change it.
        const facetQuery = useMemo(() => lineItemFacetQuery(columnFilters), [columnFilters]);
        const facetQueryKey = JSON.stringify(facetQuery);
        useEffect(() => {
            void loadFacets(file.id, facetQuery);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [file.id, facetQueryKey, loadFacets]);
        // Drop every column filter at once. useServerTable resets to page 1 on a
        // filter change, so the next fetch returns the unfiltered first page.
        const handleClearAllFilters = useCallback(() => {
            onColumnFiltersChange([]);
        }, [onColumnFiltersChange]);

        const {
            isAnyAutoPopulating,
            isAutoPopulateOpen,
            setIsAutoPopulateOpen,
            handleAutoPopulateOpenChange,
            selectedPropcode,
            selectedPropcodeOption,
            setSelectedPropcodeOption,
            autoPopulateError,
            isAutoPopulating,
            handleAutoPopulatePropcodes,
            searchPropcodesForModal,
            isSegmentPopulateOpen,
            handleSegmentPopulateOpenChange,
            selectedSegmentType,
            setSelectedSegmentType,
            segmentValue,
            setSegmentValue,
            segmentValueOption,
            setSegmentValueOption,
            segmentUpdateOnlyRequired,
            setSegmentUpdateOnlyRequired,
            segmentPopulateError,
            isSegmentPopulating,
            searchSegmentValuesForModal,
            handleAutoPopulateSegmentsSubmit,
        } = useAutoPopulate({
            lineItems,
            filteredLineItems: null,
            matchingRowCount: lineItemsMeta.total,
            activeFilterQuery,
            onAutoPopulatePropcodes,
            onAutoPopulateSegments,
            onAutoPopulatePropcodesByFilter,
            onAutoPopulateSegmentsByFilter,
            searchMasterPropertyCodes,
            searchSegmentValues,
            onRefresh,
            onSuccess: (message) => setActionNotification({ type: "success", message }),
        });

        // Status change modal state
        const [isStatusChangeOpen, setIsStatusChangeOpen] = useState(false);
        const [isChangingStatus, setIsChangingStatus] = useState(false);
        const [statusChangeError, setStatusChangeError] = useState<string | null>(null);

        // Manual reject modal state
        const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
        const [rejectionReason, setRejectionReason] = useState("");
        const [isRejecting, setIsRejecting] = useState(false);
        const [rejectError, setRejectError] = useState<string | null>(null);

        const {
            editModalOpen,
            editingLineItem,
            editFormData,
            isSaving,
            editError,
            editSegmentMappings,
            editSegmentAllowedValues,
            editSegment4Descriptions,
            isLoadingDropdowns,
            editFields,
            mergedEditFieldErrors,
            handleEditClick,
            handleFieldChange,
            handleAccountSelect,
            handlePropcodeSelect,
            searchMasterPropertyCodes: searchMasterPropertyCodesForEditor,
            searchYardiAccountsByCode: searchYardiAccountsByCodeForEditor,
            searchYardiAccountsByName: searchYardiAccountsByNameForEditor,
            handleSaveLineItem,
            handleCloseEditModal,
        } = useLineItemEditor({
            fileDealIdForEdit: file.dealId,
            fileType: file.fileType,
            getSegmentAllowedValuesBatch,
            searchMasterPropertyCodes,
            searchYardiAccountsByCode,
            searchYardiAccountsByName,
            loadDealSegmentMappings,
            onEditLineItem,
        });

        // --- Split state -----------------------------------------------------
        // Which originals are expanded to show their split rows, and which line the
        // "number of splits" modal is targeting. Expansion is local to the view; the
        // split rows themselves arrive nested on each row from the list endpoint.
        const [expandedSplits, setExpandedSplits] = useState<ExpandedState>({});
        const [splitTarget, setSplitTarget] = useState<TReviewLineItem | null>(null);
        const [isSplitting, setIsSplitting] = useState(false);
        const [splitError, setSplitError] = useState<string | null>(null);

        const handleSplitClick = useCallback((lineItem: TReviewLineItem) => {
            setSplitError(null);
            setSplitTarget(lineItem);
        }, []);

        const handleCloseSplitModal = useCallback(() => {
            setSplitTarget(null);
            setSplitError(null);
        }, []);

        const handleSplitSubmit = useCallback(
            async (count: number) => {
                if (!splitTarget) return;
                setIsSplitting(true);
                setSplitError(null);
                try {
                    await onSplitLineItem(splitTarget, count);
                    // Open the new split straight away — the amounts need editing and
                    // nothing else can proceed until they add up.
                    setExpandedSplits((prev) =>
                        prev === true ? prev : { ...prev, [splitTarget.id]: true },
                    );
                    setSplitTarget(null);
                } catch (err) {
                    setSplitError(err instanceof Error ? err.message : "Failed to split line item");
                } finally {
                    setIsSplitting(false);
                }
            },
            [splitTarget, onSplitLineItem],
        );

        const runSplitAction = useCallback(
            async (action: () => Promise<void>, failureMessage: string) => {
                setIsSplitting(true);
                try {
                    await action();
                } catch (err) {
                    setActionNotification({
                        type: "error",
                        message: err instanceof Error ? err.message : failureMessage,
                    });
                } finally {
                    setIsSplitting(false);
                }
            },
            [],
        );

        const handleReverseSplit = useCallback(
            (lineItem: TReviewLineItem) =>
                void runSplitAction(async () => {
                    await onReverseSplit(lineItem);
                    setExpandedSplits((prev) => {
                        if (prev === true) return prev;
                        const { [lineItem.id]: _removed, ...rest } = prev;
                        return rest;
                    });
                }, "Failed to reverse the split"),
            [runSplitAction, onReverseSplit],
        );

        // A split row's "add" button appends one more row to the same original, so
        // it targets the row's parent rather than the split row it was clicked on.
        const handleAddSplitRow = useCallback(
            (lineItem: TReviewLineItem) => {
                const parentId = lineItem.parentLineId;
                if (!parentId) return;
                void runSplitAction(
                    () => onSplitLineItem({ ...lineItem, id: parentId }, 1),
                    "Failed to add a split row",
                );
            },
            [runSplitAction, onSplitLineItem],
        );

        const handleDeleteSplitRow = useCallback(
            (lineItem: TReviewLineItem) =>
                void runSplitAction(
                    () => onDeleteSplitRow(lineItem),
                    "Failed to delete the split row",
                ),
            [runSplitAction, onDeleteSplitRow],
        );

        // Per-row edit gate: locked file, a background operation running on it, or
        // no line-edit permission for this file type. Deliberately NOT gated on
        // isSplitCompleted — editing a split row's amount is exactly how an
        // incomplete split gets completed, so disabling edits while one is
        // outstanding would deadlock the reviewer. `isFileOperationRunning` closes
        // a gap `file.isLocked` alone leaves open: that snapshot is only
        // as fresh as the last `loadFile`, and nothing reloads the file while an
        // operation is in flight, so without this the grid stayed editable (and a
        // cell edit would 409) for the whole run.
        const actionsDisabled =
            file.isLocked ||
            isFileOperationRunning ||
            finjournalWriteBlocked ||
            tbglWriteBlocked ||
            isFrozen;
        // Splitting needs its own permission (tbgl_line_split, Account Manager /
        // Accountant only), is not offered on standalone FinJournal files, and stops
        // once the file's output has been generated — the server decides that last
        // part because on a TB_AND_GL deal it depends on the sibling GL file.
        const canSplitLines = useCanSplitLines() && !isFinJournal && file.canSplitLineItems;
        // Starting a *new* split is blocked while another is incomplete, or while a
        // background operation is running (see actionsDisabled above); reversal and
        // the in-panel row actions stay available so the outstanding one can be fixed.
        // Gated on canSplitLines rather than the line-edit write gate: splitting has
        // its own permission, and the Product Owner holds it without holding
        // tbgl_line_edit.
        const splitDisabled =
            file.isLocked || isFileOperationRunning || !file.isSplitCompleted || !canSplitLines;
        // Narrowed facets, with each dimension's own applied values added back so an
        // active filter always stays visible (and clearable) in its own dropdown.
        const filterFacets = useMemo(
            () => withSelectedFacetValues(facets, columnFilters),
            [facets, columnFilters],
        );
        const lineItemColumns = useMemo(
            () =>
                createLineItemColumns(
                    {
                        onEdit: handleEditClick,
                        onSplit: handleSplitClick,
                        onReverseSplit: handleReverseSplit,
                        onAddSplitRow: handleAddSplitRow,
                        onDeleteSplitRow: handleDeleteSplitRow,
                    },
                    { editDisabled: actionsDisabled, canSplit: canSplitLines, splitDisabled },
                    filterFacets,
                    file.coaMappingMode === "multi_criteria",
                ),
            [
                handleEditClick,
                handleSplitClick,
                handleReverseSplit,
                handleAddSplitRow,
                handleDeleteSplitRow,
                actionsDisabled,
                canSplitLines,
                splitDisabled,
                filterFacets,
                file.coaMappingMode,
            ],
        );

        // Update selected status when file loads
        useEffect(() => {
            if (file.status) {
                setSelectedStatus(file.status);
            }
        }, [file.status]);

        const statusOptions = useMemo(
            () =>
                getStatusOptions(file.status, file.fileType, {
                    canMarkCompleted,
                    completedFromStatus: file.completedFromStatus,
                }),
            [file, canMarkCompleted],
        );

        const handleDownload = useCallback(async () => {
            try {
                await onDownload(selectedDownloadType);
            } catch (error) {
                setActionNotification({
                    type: "error",
                    message:
                        error instanceof Error
                            ? error.message
                            : "Download failed. Please try again.",
                });
            }
        }, [onDownload, selectedDownloadType]);

        const handleStatusChange = useCallback(() => {
            if (selectedStatus && selectedStatus !== file.status) {
                setStatusChangeError(null);
                setIsStatusChangeOpen(true);
            }
        }, [selectedStatus, file.status]);

        const handleConfirmStatusChange = useCallback(async () => {
            setIsChangingStatus(true);
            setStatusChangeError(null);
            try {
                await onChangeStatus(selectedStatus);
                setIsStatusChangeOpen(false);
            } catch (error) {
                setStatusChangeError(
                    error instanceof Error
                        ? error.message
                        : "Failed to change status. Please try again.",
                );
            } finally {
                setIsChangingStatus(false);
            }
        }, [onChangeStatus, selectedStatus]);

        // The error report only exists when the file has validation errors —
        // file-level (e.g. rejection) or on any line item. Hiding the option
        // otherwise mirrors the backend, which 404s on an error-free file.
        const hasReportableErrors = useMemo(
            () =>
                validationErrorsToText(file.validationErrors).length > 0 ||
                lineItems.some((item) => item.validationErrors.length > 0),
            [file.validationErrors, lineItems],
        );

        const availableDownloadTypes = useMemo(() => {
            const types = [...baseDownloadTypes];
            if (hasReportableErrors) {
                types.push({ value: "errors", label: "Error Report" });
            }
            const isTbOnly = file.dealFileType === "tb_only";
            const isTbglGlFile =
                file.dealFileType === "tb_and_gl" && file.fileType === "general_ledger";
            const isFinjournalFile = file.fileType === "financial_journals";
            const dest = file.finalFileDestination;

            if (isTbOnly || isTbglGlFile || isFinjournalFile) {
                if (
                    dest !== "data_platform" &&
                    (file.status === "finjournal_generated" || isCompleted || isFinJournalGenerated)
                ) {
                    types.push({ value: "finjournal", label: "FinJournal" });
                }
                if (
                    dest === "data_platform" &&
                    (file.status === "data_platform_generated" ||
                        isCompleted ||
                        isDataPlatformGenerated)
                ) {
                    types.push({ value: "data-platform", label: "Data Platform" });
                }
            }
            return types;
        }, [
            file,
            hasReportableErrors,
            isFinJournalGenerated,
            isDataPlatformGenerated,
            isCompleted,
        ]);

        // Keep the selection valid when options disappear (e.g. the last
        // validation error is fixed and "Error Report" is removed).
        useEffect(() => {
            if (!availableDownloadTypes.some((type) => type.value === selectedDownloadType)) {
                setSelectedDownloadType("original");
            }
        }, [availableDownloadTypes, selectedDownloadType]);

        // Contributor editor options: candidate roster merged with the file's current
        // contributors, so a stored contributor who later lost eligibility still shows
        // (and can be removed). Keyed by id to de-duplicate.
        const contributorOptionsById = new Map<string, { id: string; label: string }>();
        for (const c of file.contributors) {
            contributorOptionsById.set(c.id, { id: c.id, label: c.name });
        }
        for (const c of contributorCandidates.data) {
            contributorOptionsById.set(c.id, c);
        }
        const contributorOptions = Array.from(contributorOptionsById.values());

        // The uploader is always a default, non-removable contributor.
        const lockedContributorIds = file.uploadedById ? [file.uploadedById] : [];
        const selectedContributorIds = Array.from(
            new Set([...lockedContributorIds, ...file.contributors.map((c) => c.id)]),
        );

        // The completion override on "Change File Status" must only cover the
        // completion transition itself (complete / reopen) — not ordinary review
        // transitions, which complete_review / revert_to_review still refuse for a
        // Product Owner (they lack tbgl_line_edit / finjournal_line_edit).
        const isSelectedStatusCompletionTransition =
            selectedStatus === "completed" ||
            (file.status === "completed" && selectedStatus === file.completedFromStatus);

        const handleContributorsChange = async (ids: string[]) => {
            setIsSavingContributors(true);
            try {
                await onSetContributors(ids);
            } catch (err) {
                setActionNotification({
                    type: "error",
                    message: err instanceof Error ? err.message : "Failed to update contributors",
                });
            } finally {
                setIsSavingContributors(false);
            }
        };

        return (
            // Equal vertical rhythm between every section of the tab. Extracting this
            // body out of the shell put it one level deeper than the shell's own
            // spacing, so without this the sections butt straight up against each other.
            <div className="space-y-4">
                {/*
                 * What this file is, minus anything the page already says above the
                 * panel. The deal name is the page heading and the file type is the
                 * selected radio in the Review group, so repeating either here just
                 * pushed the useful facts — status, who owns it, which upload — further
                 * down the page.
                 */}
                <div className="w-full">
                    {/* What the file is on the left, what you can do to its status on the
                        right — the status control belongs beside the status it changes. */}
                    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                            <span className="flex items-center gap-2">
                                <span className="text-muted-foreground">Status</span>
                                <span className="font-medium" data-testid="file-status">
                                    {formatStatus(file.status)}
                                </span>
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="text-muted-foreground">Finance</span>
                                <span className="font-medium">{file.accountManager || "N/A"}</span>
                            </span>
                            <span className="flex min-w-0 items-center gap-2">
                                <span className="shrink-0 text-muted-foreground">File</span>
                                <span
                                    className="truncate font-medium"
                                    title={file.originalFilename}
                                >
                                    {file.originalFilename}
                                </span>
                            </span>
                        </div>

                        {/* Status change section (only when transitions available) */}
                        {statusOptions.length > 0 && (
                            <div className="flex items-center gap-2">
                                <select
                                    id="file-status-select"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="rounded-lg border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    {statusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleStatusChange}
                                    disabled={
                                        selectedStatus === file.status ||
                                        file.isLocked ||
                                        !file.isSplitCompleted ||
                                        isArchived ||
                                        (reviewWriteBlocked &&
                                            !(
                                                canMarkCompleted &&
                                                isSelectedStatusCompletionTransition
                                            ))
                                    }
                                    title={
                                        isArchived
                                            ? ARCHIVED_ACTION_TITLE
                                            : reviewWriteBlocked &&
                                                !(
                                                    canMarkCompleted &&
                                                    isSelectedStatusCompletionTransition
                                                )
                                              ? "You do not have permission to change this file's status"
                                              : file.isLocked || !file.isSplitCompleted
                                                ? "Unavailable while file is locked or split is incomplete"
                                                : undefined
                                    }
                                >
                                    Change File Status
                                </Button>
                            </div>
                        )}
                    </div>
                    {(file.validationErrors || file.validationWarnings) && (
                        <div className="space-y-3 px-4 py-3">
                            {file.validationErrors &&
                                (() => {
                                    const bullets = validationErrorsToText(file.validationErrors);
                                    if (bullets.length === 0) return null;
                                    return (
                                        <div
                                            className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm"
                                            role="alert"
                                        >
                                            <div className="flex gap-3">
                                                <AlertCircle
                                                    className="h-5 w-5 shrink-0 text-destructive"
                                                    aria-hidden
                                                />
                                                <div className="min-w-0 space-y-2">
                                                    <p className="font-medium text-destructive">
                                                        Validation errors
                                                    </p>
                                                    <div className="list-disc list-inside space-y-1.5 text-muted-foreground">
                                                        {bullets.map((msg, i) => (
                                                            <p key={i}>{msg}</p>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            <WarningsPanel warnings={parseMessageList(file.validationWarnings)} />
                        </div>
                    )}
                </div>

                <ValidationOutcomePanel
                    file={{
                        status: file.status as TApiFileStatus,
                        rejectionReason: file.rejectionReason,
                        rejectedById: file.rejectedById,
                        rejectedByName: file.rejectedByName,
                        rejectedAt: file.rejectedAt,
                    }}
                />

                {/* Archived: read-only for everyone, and there is no way back from
                    here — the file returns only if its deal is reactivated. */}
                {isArchived && (
                    <div
                        className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700"
                        role="note"
                        data-testid="file-archived-banner"
                    >
                        <strong>File is archived</strong>
                        {file.archivedAt
                            ? ` — archived on ${formatDateTimeEuropeBerlin(file.archivedAt)}.`
                            : "."}{" "}
                        It is read-only for everyone: line items can be viewed and the file and any
                        generated export can be downloaded, but no edits or actions are possible.
                    </div>
                )}

                {/* Lock / split incomplete banner. Keyed off isOperationLocked rather
                    than file.isLocked alone: file.isLocked is a snapshot from the
                    last loadFile, and nothing reloads the file while a background
                    operation runs, so a run started after the last load would
                    otherwise show no banner at all despite the grid being disabled
                    (see actionsDisabled/splitDisabled above). effectiveLockReason
                    falls back to the running operation's name so the same
                    Committed/Revalidating/generic branches below still apply. */}
                {(() => {
                    const isOperationLocked = file.isLocked || isFileOperationRunning;
                    const effectiveLockReason = file.isLocked
                        ? file.lockReason
                        : (operationStatus?.operation ?? null);
                    return (
                        <>
                            {isOperationLocked && effectiveLockReason === "Committed" && (
                                <div
                                    className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700"
                                    role="note"
                                >
                                    <strong>OTB committed</strong> — Deal is now live. This file is
                                    locked and cannot be modified.
                                </div>
                            )}
                            {isOperationLocked && effectiveLockReason === "Revalidating" && (
                                <div
                                    className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-700"
                                    role="status"
                                    data-testid="file-revalidating-banner"
                                >
                                    <strong>Revalidating with new master data</strong> — this file
                                    is being re-checked against the latest Chart of Accounts,
                                    Property Codes, Segments and Books. Edits and actions are
                                    disabled until it finishes.
                                </div>
                            )}
                            {isOperationLocked &&
                                effectiveLockReason !== "Committed" &&
                                effectiveLockReason !== "Revalidating" && (
                                    <div
                                        className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                                        role="status"
                                    >
                                        <strong>File is locked</strong>
                                        {effectiveLockReason
                                            ? ` (${
                                                  OPERATION_LABELS[effectiveLockReason] ??
                                                  effectiveLockReason
                                              } in progress).`
                                            : " Another operation is in progress."}{" "}
                                        Edits and actions are disabled until the operation
                                        completes.
                                    </div>
                                )}
                        </>
                    );
                })()}
                {!file.isLocked && !isFileOperationRunning && !file.isSplitCompleted && (
                    <div
                        className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                        role="status"
                    >
                        <strong>Split incomplete.</strong> Complete or remove split children so that
                        each parent&apos;s child amounts sum correctly and no child has amount zero.
                        Some actions are disabled until splits are complete.
                    </div>
                )}

                {/*
                 * Delivery and action outcomes, above the whole Line Items section.
                 *
                 * These answer "what just happened to this file" — a question that
                 * belongs before the data, not tucked into the toolbar among the
                 * controls that caused it. The Yardi delivery state was previously a
                 * small chip beside the Send-to-Replica button, which put the most
                 * consequential outcome in the least prominent place on the page.
                 */}
                {actionNotification && (
                    <div
                        className={cn(
                            "flex items-center gap-2 rounded-lg border px-4 py-3 text-sm",
                            actionNotification.type === "success"
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-red-200 bg-red-50 text-red-700",
                        )}
                    >
                        {actionNotification.type === "success" ? (
                            <CheckCircle className="h-4 w-4 flex-shrink-0" />
                        ) : (
                            <XCircle className="h-4 w-4 flex-shrink-0" />
                        )}
                        <span className="flex-1">{actionNotification.message}</span>
                        <button
                            onClick={() => setActionNotification(null)}
                            className="text-current opacity-70 hover:opacity-100"
                            aria-label="Dismiss"
                        >
                            <XCircle className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {(latestReplica?.status === "failed" || latestReplica?.status === "rejected") && (
                    <div
                        data-testid="sftp-submission-failed-banner"
                        className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                        <XCircle className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1">
                            {latestReplica.status === "rejected"
                                ? "Yardi rejected the file"
                                : "Replica send failed"}
                            {latestReplica.errorMessage ? `: ${latestReplica.errorMessage}` : "."}
                        </span>
                    </div>
                )}

                {/* Same visibility rule the chip had: the failed and rejected states are
                    the banner above's job, and "queued" is too transient to announce. */}
                {latestReplica &&
                    latestReplica.status !== "queued" &&
                    latestReplica.status !== "failed" &&
                    latestReplica.status !== "rejected" && (
                        <div
                            data-testid="sftp-submission-status"
                            title={latestReplica.errorMessage ?? undefined}
                            className={cn(
                                "flex items-center gap-2 rounded-lg border px-4 py-3 text-sm",
                                latestReplica.status === "uploaded" ||
                                    latestReplica.status === "processed"
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-amber-200 bg-amber-50 text-amber-800",
                            )}
                        >
                            {latestReplica.status === "uploaded" ||
                            latestReplica.status === "processed" ? (
                                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                            ) : (
                                <RefreshCw className="h-4 w-4 flex-shrink-0 animate-spin" />
                            )}
                            <span className="flex-1">
                                {
                                    {
                                        queued: "Queued for SFTP upload",
                                        waiting_for_window: "Waiting for upload window",
                                        uploading: "Uploading to SFTP",
                                        uploaded: "Sent to Replica",
                                        failed: "Replica send failed",
                                        processed: "Processed by Yardi",
                                        rejected: "Rejected by Yardi",
                                    }[latestReplica.status]
                                }
                            </span>
                        </div>
                    )}

                {/* Line Items Table */}
                <div className="w-full rounded-xl border bg-background">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                        <h3 className="text-sm font-semibold flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            Line Items
                            {/* The count belongs on this line, not on a second row of its
                                own beneath it — DataTable's own count bar is switched off
                                below so it is not said twice. */}
                            {!isLoadingLineItems && (
                                <span className="font-normal text-muted-foreground tabular-nums">
                                    {lineItemCountLabel}
                                </span>
                            )}
                        </h3>
                        {/* Clearing filters belongs with the count it changes, not stranded
                            under a table that can be 820px tall. A text action rather than a
                            button: it is one of two things on this line and should not compete
                            with the heading. Muted while disabled, so it reads as unavailable
                            rather than missing. */}
                        <button
                            type="button"
                            onClick={handleClearAllFilters}
                            disabled={!hasActiveLineItemFilters}
                            className="text-sm font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                        >
                            Clear all filters
                        </button>
                    </div>

                    {/* Action Buttons Row — mutation buttons disabled when locked or split incomplete */}
                    {(() => {
                        // Heavier write verbs (reject / auto-populate) require the run-actions
                        // capability for TB/GL files (Account Manager / Accountant only).
                        // isFileOperationRunning closes the same gap it does for the grid's
                        // actionsDisabled above: auto-populate itself is one of handleRefresh's
                        // callers, and handleRefresh now takes the revalidate lock, so without
                        // this these buttons stayed enabled (and 409'd) for the run's duration.
                        const actionsDisabled =
                            file.isLocked ||
                            isFileOperationRunning ||
                            !file.isSplitCompleted ||
                            finjournalWriteBlocked ||
                            tbglActionsBlocked ||
                            isFrozen;
                        // Whole-file gating (server aggregates) — not just the current page.
                        const allLineItemsValid = file.allLineItemsValid;
                        const hasRebalanceRows = file.hasRebalanceRows;
                        const canAutoBalance =
                            canAutoBalance_ &&
                            file.status === "ready_for_balancing" &&
                            !file.isLocked &&
                            file.isSplitCompleted &&
                            !isViewingAsAdmin &&
                            allLineItemsValid &&
                            !hasRebalanceRows;

                        // Remove Generated Lines availability:
                        // - File status is NOT 'not_received', 'uploaded', 'finjournal_generated',
                        //   or 'data_platform_generated' — the backend's EXCLUDED_STATUSES refuses
                        //   deleting generated lines from an already-exported period.
                        // - File is not locked
                        const excludedStatusesForRemove = [
                            "not_received",
                            "uploaded",
                            "finjournal_generated",
                            "data_platform_generated",
                        ];
                        const canRemoveGeneratedLines =
                            !excludedStatusesForRemove.includes(file.status) &&
                            !file.isLocked &&
                            !isViewingAsAdmin &&
                            !tbglActionsBlocked &&
                            !isFrozen;

                        // Adjustment availability:
                        // - File must be GL type
                        // - Deal must be TB_AND_GL
                        // - File is not locked
                        const canRunAdjustment =
                            file.fileType === "general_ledger" &&
                            file.dealFileType === "tb_and_gl" &&
                            file.status === "balanced" &&
                            !file.isLocked &&
                            !tbglActionsBlocked &&
                            !isFrozen;

                        // GL generation is available for TB-only when no invalid line items
                        // (whole file, not just the current page).
                        const hasInvalidLineItems = file.invalidLineItemCount > 0;
                        const canGenerateGL =
                            file.dealFileType === "tb_only" &&
                            !file.isLocked &&
                            file.isSplitCompleted &&
                            !hasInvalidLineItems &&
                            !tbglActionsBlocked &&
                            !isFrozen;

                        const handleAutoBalance = async () => {
                            setIsAutoBalancing(true);
                            try {
                                await onAutoBalance();
                            } catch (error) {
                                console.error("Auto-balance error:", error);
                                // Show user-friendly error message
                                const message =
                                    error instanceof Error
                                        ? error.message
                                        : "Auto-balance failed. Please try again.";
                                alert(message);
                            } finally {
                                setIsAutoBalancing(false);
                            }
                        };

                        const handleCommitOTB = async () => {
                            setIsCommitting(true);
                            setActionNotification(null);
                            try {
                                await onCommitOTB();
                                setActionNotification({
                                    type: "success",
                                    message: "OTB committed successfully. Deal is now live.",
                                });
                                onRefresh();
                            } catch (error) {
                                console.error("OTB commit error:", error);
                                const message =
                                    error instanceof Error
                                        ? error.message
                                        : "Failed to commit OTB. Please try again.";
                                setActionNotification({ type: "error", message });
                            } finally {
                                setIsCommitting(false);
                            }
                        };

                        const handleRemoveGeneratedLines = async () => {
                            setIsRemovingGeneratedLines(true);
                            setActionNotification(null);
                            try {
                                const result = await onRemoveGeneratedLines();
                                // Show success notification
                                setActionNotification({
                                    type: "success",
                                    message: result.message,
                                });
                                // Refresh the page to show updated line items
                                onRefresh();
                            } catch (error) {
                                console.error("Remove generated lines error:", error);
                                // Show user-friendly error message
                                const message =
                                    error instanceof Error
                                        ? error.message
                                        : "Failed to remove generated lines. Please try again.";
                                setActionNotification({
                                    type: "error",
                                    message,
                                });
                            } finally {
                                setIsRemovingGeneratedLines(false);
                            }
                        };

                        const handleRunAdjustment = async () => {
                            setIsRunningAdjustment(true);
                            setActionNotification(null);
                            try {
                                const result = await onRunAdjustment();
                                // Show success notification. The backend result no longer
                                // carries a message for this operation, so build one here.
                                setActionNotification({
                                    type: "success",
                                    message: `Adjustment complete: ${result.adjustments_created} adjustment${
                                        result.adjustments_created === 1 ? "" : "s"
                                    } created.`,
                                });
                                // Refresh the page to show updated line items and status
                                onRefresh();
                            } catch (error) {
                                console.error("Run adjustment error:", error);
                                // Show user-friendly error message
                                const message =
                                    error instanceof Error
                                        ? error.message
                                        : "Failed to run adjustment. Please try again.";
                                setActionNotification({
                                    type: "error",
                                    message,
                                });
                            } finally {
                                setIsRunningAdjustment(false);
                            }
                        };

                        const runRefresh = async (reparse: boolean) => {
                            setIsRefreshing(true);
                            setActionNotification(null);
                            try {
                                await onManualRefresh(reparse);
                                setActionNotification({
                                    type: "success",
                                    message: reparse
                                        ? "File re-read from source with the deal's current " +
                                          "column mappings and row filtering rules."
                                        : "File refreshed and re-validated against the latest data.",
                                });
                            } catch (err) {
                                setActionNotification({
                                    type: "error",
                                    message:
                                        err instanceof Error && err.message
                                            ? err.message
                                            : "Failed to refresh the file. Please try again.",
                                });
                            } finally {
                                setIsRefreshing(false);
                            }
                        };

                        runRefreshRef.current = runRefresh;

                        // Row-filtering rules and column mappings are applied while parsing,
                        // so picking them up means re-reading the source file — which
                        // rebuilds every non-generated line item. Confirm first when that
                        // would discard manual edits or splits; otherwise just do it.
                        const handleRefreshClick = async () => {
                            if (isRefreshing || refreshBlocked) return;
                            if (!parseConfigChanged) {
                                await runRefresh(false);
                                return;
                            }
                            if (manualEditCount > 0 || splitLineCount > 0) {
                                setIsReparseConfirmOpen(true);
                                return;
                            }
                            await runRefresh(true);
                        };

                        const handleGenerateGL = async () => {
                            setIsGeneratingGL(true);
                            try {
                                await onGenerateGL();
                            } catch (err) {
                                const message =
                                    err instanceof Error ? err.message : "Failed to generate GL";
                                setGLGenerationError(message);
                            } finally {
                                setIsGeneratingGL(false);
                            }
                        };

                        const handleGenerateFinJournal = async () => {
                            setIsGeneratingFinJournal(true);
                            try {
                                await onGenerateFinJournal();
                                setIsFinJournalGenerated(true);
                                setActionNotification({
                                    type: "success",
                                    message:
                                        "FinJournal generated successfully. Select 'FinJournal' from the download dropdown.",
                                });
                            } catch (err) {
                                const message =
                                    err instanceof Error
                                        ? err.message
                                        : "Failed to generate FinJournal";
                                setActionNotification({ type: "error", message });
                            } finally {
                                setIsGeneratingFinJournal(false);
                            }
                        };

                        const handleGenerateDataPlatform = async () => {
                            setIsGeneratingDataPlatform(true);
                            try {
                                await onGenerateDataPlatform();
                                setIsDataPlatformGenerated(true);
                                setActionNotification({
                                    type: "success",
                                    message:
                                        "Data Platform generated successfully. Select 'Data Platform' from the download dropdown.",
                                });
                            } catch (err) {
                                const message =
                                    err instanceof Error
                                        ? err.message
                                        : "Failed to generate Data Platform";
                                setActionNotification({ type: "error", message });
                            } finally {
                                setIsGeneratingDataPlatform(false);
                            }
                        };

                        const canGenerateFinJournal =
                            finjournalCaps.canGenerate &&
                            ((file.dealFileType === "tb_only" && file.status === "balanced") ||
                                (file.fileType === "open_trial_balance" &&
                                    file.status === "committed") ||
                                (file.dealFileType === "tb_and_gl" &&
                                    file.fileType === "general_ledger" &&
                                    file.status === "adjusted") ||
                                (file.fileType === "financial_journals" &&
                                    file.status === "balanced")) &&
                            !file.isLocked &&
                            file.finalFileDestination !== "data_platform";

                        const canGenerateDataPlatform =
                            ((file.dealFileType === "tb_only" && file.status === "balanced") ||
                                (file.fileType === "open_trial_balance" &&
                                    file.status === "committed") ||
                                (file.dealFileType === "tb_and_gl" &&
                                    file.fileType === "general_ledger" &&
                                    file.status === "adjusted")) &&
                            !file.isLocked &&
                            file.finalFileDestination === "data_platform";

                        return (
                            <div className="border-b px-4 py-2.5">
                                <ScrollableRow aria-label="File actions" className="gap-2 pb-1">
                                    {file.dealFileType !== "tb_only" &&
                                        file.fileType !== "open_trial_balance" && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={handleAutoBalance}
                                                disabled={
                                                    !canAutoBalance ||
                                                    isAutoBalancing ||
                                                    isFileOperationRunning
                                                }
                                                title={
                                                    isFileOperationRunning
                                                        ? "Another operation is already running on this file"
                                                        : hasRebalanceRows
                                                          ? "Auto-balance already completed"
                                                          : !canAutoBalance
                                                            ? "Requires: Ready for balancing, not locked, split complete, not admin, and all line items valid"
                                                            : "Balance transaction groups to zero"
                                                }
                                            >
                                                {isAutoBalancing
                                                    ? "Auto balancing…"
                                                    : "Auto balance"}
                                            </Button>
                                        )}
                                    {/* Adjust sits between balancing and generation: it is the step
                                        that reconciles a balanced GL against the TB before the
                                        FinJournal is generated from it. */}
                                    {file.dealFileType === "tb_and_gl" &&
                                        file.fileType === "general_ledger" && (
                                            <Button
                                                size="sm"
                                                data-testid="run-adjustment-button"
                                                variant="outline"
                                                onClick={handleRunAdjustment}
                                                disabled={
                                                    !canRunAdjustment ||
                                                    isRunningAdjustment ||
                                                    isFileOperationRunning
                                                }
                                                title={
                                                    isFileOperationRunning
                                                        ? "Another operation is already running on this file"
                                                        : !canRunAdjustment
                                                          ? "Unavailable: requires GL file, TB_AND_GL deal, and not locked"
                                                          : "Run adjustment to reconcile GL with TB data"
                                                }
                                            >
                                                {isRunningAdjustment ? "Adjusting…" : "Adjust"}
                                            </Button>
                                        )}
                                    {file.fileType === "open_trial_balance" && (
                                        <Button
                                            size="sm"
                                            data-testid="commit-otb-button"
                                            variant="outline"
                                            onClick={handleCommitOTB}
                                            disabled={
                                                file.status !== "ready_for_commitment" ||
                                                !file.isSplitCompleted ||
                                                file.isLocked ||
                                                isCommitting ||
                                                isArchived
                                            }
                                            title={
                                                file.status === "committed"
                                                    ? "OTB already committed"
                                                    : file.status !== "ready_for_commitment"
                                                      ? "OTB must be ready for commitment"
                                                      : !file.isSplitCompleted
                                                        ? "All splits must be completed"
                                                        : file.isLocked
                                                          ? "File is locked"
                                                          : "Commit this OTB to make the deal live"
                                            }
                                        >
                                            {isCommitting ? "Committing…" : "Commit OTB"}
                                        </Button>
                                    )}
                                    {file.dealFileType === "tb_only" &&
                                        file.fileType !== "open_trial_balance" && (
                                            <Button
                                                size="sm"
                                                data-testid="generate-gl-button"
                                                variant="outline"
                                                onClick={handleGenerateGL}
                                                disabled={
                                                    !canGenerateGL ||
                                                    isGeneratingGL ||
                                                    isFileOperationRunning ||
                                                    file.status === "balanced" ||
                                                    file.status === "finjournal_generated" ||
                                                    file.status === "data_platform_generated"
                                                }
                                                title={
                                                    isFileOperationRunning
                                                        ? "Another operation is already running on this file"
                                                        : file.status === "balanced" ||
                                                            file.status ===
                                                                "finjournal_generated" ||
                                                            file.status ===
                                                                "data_platform_generated"
                                                          ? "GL already generated"
                                                          : hasInvalidLineItems
                                                            ? "Fix or remove invalid line items before generating GL"
                                                            : !canGenerateGL
                                                              ? "Requires: TB-only deal, not locked, split complete"
                                                              : "Generate GL movement lines from Trial Balance"
                                                }
                                            >
                                                {isGeneratingGL ? "Generating GL…" : "Generate GL"}
                                            </Button>
                                        )}
                                    {file.finalFileDestination === "data_platform" &&
                                        (file.dealFileType !== "tb_and_gl" ||
                                            file.fileType === "general_ledger") && (
                                            <Button
                                                size="sm"
                                                data-testid="generate-data-platform-button"
                                                variant="outline"
                                                onClick={handleGenerateDataPlatform}
                                                disabled={
                                                    !canGenerateDataPlatform ||
                                                    isGeneratingDataPlatform ||
                                                    isDataPlatformGenerated
                                                }
                                                title={
                                                    isDataPlatformGenerated
                                                        ? "Data Platform already generated"
                                                        : !canGenerateDataPlatform
                                                          ? "Requires: GL generated status"
                                                          : "Generate Data Platform download from GL lines"
                                                }
                                            >
                                                {isGeneratingDataPlatform
                                                    ? "Generating Data Platform…"
                                                    : "Generate Data Platform"}
                                            </Button>
                                        )}
                                    {file.finalFileDestination !== "data_platform" &&
                                        (file.dealFileType !== "tb_and_gl" ||
                                            file.fileType === "general_ledger") && (
                                            <Button
                                                size="sm"
                                                data-testid="generate-finjournal-button"
                                                variant="outline"
                                                onClick={handleGenerateFinJournal}
                                                disabled={
                                                    !canGenerateFinJournal ||
                                                    isGeneratingFinJournal ||
                                                    isFinJournalGenerated
                                                }
                                                title={
                                                    isFinJournalGenerated
                                                        ? "FinJournal already generated"
                                                        : !canGenerateFinJournal
                                                          ? "Requires: GL generated status"
                                                          : "Generate FinJournal download from GL lines"
                                                }
                                            >
                                                {isGeneratingFinJournal
                                                    ? "Generating FinJournal…"
                                                    : "Generate FinJournal"}
                                            </Button>
                                        )}
                                    {file.finalFileDestination !== "data_platform" &&
                                        (file.dealFileType !== "tb_and_gl" ||
                                            file.fileType === "general_ledger") &&
                                        (() => {
                                            const isReplicaInFlight =
                                                latestReplica !== undefined &&
                                                [
                                                    "queued",
                                                    "waiting_for_window",
                                                    "uploading",
                                                ].includes(latestReplica.status);
                                            const canSendToReplica =
                                                finjournalCaps.canGenerate &&
                                                file.status === "finjournal_generated" &&
                                                !file.isLocked &&
                                                !isReplicaInFlight;

                                            const handleSendToReplica = async () => {
                                                setIsSendingToReplica(true);
                                                setActionNotification(null);
                                                try {
                                                    await onSendToReplica();
                                                    setActionNotification({
                                                        type: "success",
                                                        message:
                                                            "File queued for delivery to Replica.",
                                                    });
                                                } catch (err) {
                                                    const message =
                                                        err instanceof Error
                                                            ? err.message
                                                            : "Failed to send the file to Replica";
                                                    setActionNotification({
                                                        type: "error",
                                                        message,
                                                    });
                                                } finally {
                                                    setIsSendingToReplica(false);
                                                }
                                            };

                                            return (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        data-testid="send-to-replica-button"
                                                        variant="outline"
                                                        onClick={() => {
                                                            void handleSendToReplica();
                                                        }}
                                                        disabled={
                                                            !canSendToReplica ||
                                                            isSendingToReplica ||
                                                            isFrozen
                                                        }
                                                        title={
                                                            isArchived
                                                                ? ARCHIVED_ACTION_TITLE
                                                                : isCompleted
                                                                  ? "File is completed — reopen it to send again"
                                                                  : isReplicaInFlight
                                                                    ? "A submission is already in progress"
                                                                    : file.status !==
                                                                        "finjournal_generated"
                                                                      ? "Generate the FinJournal first"
                                                                      : "Queue the FinJournal for SFTP delivery to Replica"
                                                        }
                                                    >
                                                        {isSendingToReplica
                                                            ? "Sending…"
                                                            : latestReplica?.status === "failed" ||
                                                                latestReplica?.status === "rejected"
                                                              ? "Re-send to Replica"
                                                              : "Send to Replica"}
                                                    </Button>
                                                </>
                                            );
                                        })()}
                                    <Button
                                        size="sm"
                                        data-testid="manual-reject-button"
                                        variant="outline"
                                        onClick={() => setIsRejectModalOpen(true)}
                                        disabled={
                                            file.isLocked ||
                                            isFileOperationRunning ||
                                            !file.isSplitCompleted ||
                                            isArchived ||
                                            (reviewWriteBlocked && !canForceReject)
                                        }
                                        title={
                                            isArchived
                                                ? ARCHIVED_ACTION_TITLE
                                                : file.isLocked ||
                                                    isFileOperationRunning ||
                                                    !file.isSplitCompleted
                                                  ? "Unavailable while file is locked or split is incomplete"
                                                  : "Reject this file with a reason"
                                        }
                                    >
                                        Manual Reject
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setIsAutoPopulateOpen(true)}
                                        disabled={actionsDisabled || isAnyAutoPopulating}
                                        title={
                                            actionsDisabled
                                                ? "Unavailable while file is locked or split is incomplete"
                                                : isAnyAutoPopulating
                                                  ? "An auto-population is already running. Please wait for it to finish."
                                                  : undefined
                                        }
                                    >
                                        Auto-populate Propcodes
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleSegmentPopulateOpenChange(true)}
                                        disabled={actionsDisabled || isAnyAutoPopulating}
                                        title={
                                            actionsDisabled
                                                ? "Unavailable while file is locked or split is incomplete"
                                                : isAnyAutoPopulating
                                                  ? "An auto-population is already running. Please wait for it to finish."
                                                  : undefined
                                        }
                                    >
                                        Auto-populate Segments
                                    </Button>
                                    <Button
                                        size="sm"
                                        data-testid="remove-generated-lines-button"
                                        variant="outline"
                                        onClick={handleRemoveGeneratedLines}
                                        disabled={
                                            !canRemoveGeneratedLines ||
                                            isRemovingGeneratedLines ||
                                            isFileOperationRunning
                                        }
                                        title={
                                            isFileOperationRunning
                                                ? "Another operation is already running on this file"
                                                : !canRemoveGeneratedLines
                                                  ? "Unavailable: requires valid status (not 'not_received', 'uploaded', 'finjournal_generated', 'data_platform_generated', or 'completed'), not locked, and not viewing as admin"
                                                  : "Remove auto-generated lines (Rebalance, Adjustment, GL Generated Movement)"
                                        }
                                    >
                                        {isRemovingGeneratedLines
                                            ? "Removing…"
                                            : "Remove Generated Lines"}
                                    </Button>
                                    <Button
                                        size="sm"
                                        data-testid="refresh-file-button"
                                        variant="outline"
                                        onClick={handleRefreshClick}
                                        disabled={
                                            isRefreshing ||
                                            refreshBlocked ||
                                            isFrozen ||
                                            isFileOperationRunning
                                        }
                                        title={
                                            isArchived
                                                ? ARCHIVED_ACTION_TITLE
                                                : isFileOperationRunning
                                                  ? "Another operation is already running on this file"
                                                  : isCompleted
                                                    ? "File is completed — reopen it to refresh"
                                                    : refreshBlocked
                                                      ? "Requires Account Manager or Accountant role"
                                                      : parseConfigChanged
                                                        ? "The deal's column mappings or row filtering rules changed — this re-reads the source file"
                                                        : "Re-validate this file against the latest CoA and mappings"
                                        }
                                    >
                                        <RefreshCw
                                            className={cn(
                                                "h-4 w-4 mr-2",
                                                isRefreshing && "animate-spin",
                                            )}
                                        />
                                        {isRefreshing ? "Refreshing…" : "Refresh"}
                                    </Button>
                                </ScrollableRow>
                            </div>
                        );
                    })()}

                    <div className="border-b border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
                        We strongly recommend reviewing the CoA mapping before amending any missing
                        segments, as using the &ldquo;Refresh&rdquo; action will remove all manual
                        corrections.
                    </div>

                    <DataTable
                        columns={lineItemColumns}
                        storageKey="file-review-line-items"
                        data={lineItems}
                        loading={isLoadingLineItems}
                        emptyMessage="No line items found for this file."
                        enableFiltering
                        enableColumnResizing
                        manualFiltering
                        manualSorting
                        columnFilters={columnFilters}
                        onColumnFiltersChange={onColumnFiltersChange}
                        sortingState={sorting}
                        onSortingChange={onSortingChange}
                        maxHeight={820}
                        className="border rounded-lg"
                        rowNoun={{ one: "line item", many: "line items" }}
                        // Rendered on the "Line Items" heading instead — see above.
                        showRowCount={false}
                        rowCount={lineItemRowCount}
                        pagination={{
                            page,
                            pageSize: lineItemsMeta.pageSize || FILE_REVIEW_PAGE_SIZE,
                            total: lineItemsMeta.total,
                            onPageChange: setPage,
                        }}
                        // Split rows render as real sub-rows so they reuse the same
                        // column defs and resolved widths as their original.
                        getSubRows={getSplitSubRows}
                        expanded={expandedSplits}
                        onExpandedChange={setExpandedSplits}
                        rowClassName={splitRowClassName}
                        renderRowBanner={renderSplitBanner}
                    />
                </div>

                <Modal
                    open={isAutoPopulateOpen}
                    onOpenChange={handleAutoPopulateOpenChange}
                    title="Auto-Populate Propcodes"
                    description="Assign the selected propcode to all line items matching the current filter."
                >
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="auto-populate-propcode">Propcode</Label>
                            <SearchableSelect
                                id="auto-populate-propcode"
                                value={selectedPropcodeOption}
                                searchOnOpen
                                onSearch={searchPropcodesForModal}
                                onSelect={setSelectedPropcodeOption}
                                placeholder="Search or select propcode..."
                            />
                        </div>
                        <div className="text-sm text-muted-foreground">
                            Filtered line items:{" "}
                            <span className="font-medium text-foreground">
                                {filteredLineItemCount}
                            </span>
                        </div>
                        {autoPopulateError && (
                            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                                {autoPopulateError}
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => handleAutoPopulateOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAutoPopulatePropcodes}
                                disabled={
                                    isAnyAutoPopulating ||
                                    !selectedPropcode ||
                                    filteredLineItemCount === 0
                                }
                            >
                                {isAutoPopulating ? "Auto-Populating..." : "Auto-Populate"}
                            </Button>
                        </div>
                    </div>
                </Modal>

                <Modal
                    open={isSegmentPopulateOpen}
                    onOpenChange={handleSegmentPopulateOpenChange}
                    title="Auto-Populate Segments"
                    description="Bulk-fill a segment (1–7) across all line items matching the current filter."
                    className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                >
                    <div className="overflow-y-auto flex-1 min-h-[220px] -mx-6 px-6">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="segment-type">Segment</Label>
                                <select
                                    id="segment-type"
                                    value={selectedSegmentType}
                                    onChange={(e) => {
                                        setSelectedSegmentType(Number(e.target.value));
                                        setSegmentValue("");
                                        setSegmentValueOption(null);
                                    }}
                                    className="w-full px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value={1}>Segment 1 (Recoverability)</option>
                                    <option value={2}>Segment 2 (Schedule)</option>
                                    <option value={3}>Segment 3 (Tenant)</option>
                                    <option value={4}>Segment 4 (Inter-Company)</option>
                                    <option value={5}>Segment 5 (Movements)</option>
                                    <option value={6}>Segment 6 (Analytical)</option>
                                    <option value={7}>Segment 7 (Deal)</option>
                                </select>
                            </div>
                            {(selectedSegmentType === 1 || selectedSegmentType === 3) && (
                                <div className="space-y-2">
                                    <Label htmlFor="segment-value-text">Value (free text)</Label>
                                    <Input
                                        id="segment-value-text"
                                        value={segmentValue}
                                        onChange={(e) => setSegmentValue(e.target.value)}
                                        placeholder="Enter value..."
                                        className="w-full"
                                    />
                                </div>
                            )}
                            {[2, 4, 5, 6, 7].includes(selectedSegmentType) && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="segment-value-select">Value</Label>
                                        <SearchableSelect
                                            id="segment-value-select"
                                            value={segmentValueOption}
                                            searchOnOpen
                                            onSearch={(query) =>
                                                searchSegmentValuesForModal(
                                                    selectedSegmentType,
                                                    query,
                                                )
                                            }
                                            onSelect={(opt) => {
                                                setSegmentValueOption(opt);
                                                setSegmentValue(opt ? opt.value : "");
                                            }}
                                            placeholder="Search or select value..."
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="segment-update-only-required"
                                            checked={segmentUpdateOnlyRequired}
                                            onChange={(e) =>
                                                setSegmentUpdateOnlyRequired(e.target.checked)
                                            }
                                            className="rounded border-input"
                                        />
                                        <Label
                                            htmlFor="segment-update-only-required"
                                            className="font-normal cursor-pointer"
                                        >
                                            Update only required (only line items where this segment
                                            is mandatory)
                                        </Label>
                                    </div>
                                </>
                            )}
                            <div className="text-sm text-muted-foreground">
                                Filtered line items:{" "}
                                <span className="font-medium text-foreground">
                                    {filteredLineItemCount}
                                </span>
                            </div>
                            {segmentPopulateError && (
                                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                                    {segmentPopulateError}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4 border-t mt-4 shrink-0">
                        <Button
                            variant="outline"
                            onClick={() => handleSegmentPopulateOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAutoPopulateSegmentsSubmit}
                            disabled={
                                isSegmentPopulating ||
                                ([2, 4, 5, 6, 7].includes(selectedSegmentType) &&
                                    !segmentValue.trim()) ||
                                filteredLineItemCount === 0
                            }
                        >
                            {isSegmentPopulating ? "Auto-Populating..." : "Auto-Populate"}
                        </Button>
                    </div>
                </Modal>

                {/* Manual Reject Modal */}
                <Modal
                    open={isReparseConfirmOpen}
                    onOpenChange={(open) => {
                        if (!isRefreshing) setIsReparseConfirmOpen(open);
                    }}
                    title="Re-read this file from source?"
                    description={
                        "The deal's column mappings or row filtering rules changed since " +
                        "this file was processed. Applying them means re-reading the " +
                        "original file, which rebuilds its rows."
                    }
                >
                    <div className="space-y-4">
                        <div className="text-sm bg-amber-50 text-amber-900 p-3 rounded-md space-y-1">
                            <p className="font-medium">
                                This will be rebuilt from the source file:
                            </p>
                            <ul className="list-disc pl-5">
                                {manualEditCount > 0 && (
                                    <li>
                                        {manualEditCount} manually edited{" "}
                                        {manualEditCount === 1 ? "line" : "lines"}
                                    </li>
                                )}
                                {splitLineCount > 0 && (
                                    <li>
                                        {splitLineCount} split{" "}
                                        {splitLineCount === 1 ? "row" : "rows"}
                                    </li>
                                )}
                            </ul>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsReparseConfirmOpen(false)}
                                disabled={isRefreshing}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="confirm-reparse-button"
                                onClick={() => {
                                    setIsReparseConfirmOpen(false);
                                    void runRefreshRef.current?.(true);
                                }}
                                disabled={isRefreshing}
                            >
                                Re-read and rebuild
                            </Button>
                        </div>
                    </div>
                </Modal>

                <Modal
                    open={isRejectModalOpen}
                    onOpenChange={(open) => {
                        if (!isRejecting) {
                            setIsRejectModalOpen(open);
                            if (!open) {
                                setRejectionReason("");
                                setRejectError(null);
                            }
                        }
                    }}
                    title="Reject File"
                    description="Provide a reason for rejecting this file. The service provider will be notified."
                >
                    <div className="space-y-4">
                        {rejectError && (
                            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                {rejectError}
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="rejection-reason">Reason for rejection</Label>
                            <textarea
                                id="rejection-reason"
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Describe why this file is being rejected..."
                                className="w-full min-h-[120px] px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                                disabled={isRejecting}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsRejectModalOpen(false);
                                    setRejectionReason("");
                                    setRejectError(null);
                                }}
                                disabled={isRejecting}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="confirm-reject-button"
                                variant="destructive"
                                onClick={async () => {
                                    setIsRejecting(true);
                                    setRejectError(null);
                                    try {
                                        await onManualReject(rejectionReason.trim());
                                        setIsRejectModalOpen(false);
                                        setRejectionReason("");
                                    } catch (error) {
                                        setRejectError(
                                            error instanceof Error
                                                ? error.message
                                                : "Failed to reject file",
                                        );
                                    } finally {
                                        setIsRejecting(false);
                                    }
                                }}
                                disabled={isRejecting || rejectionReason.trim().length === 0}
                            >
                                {isRejecting ? "Rejecting..." : "Confirm Reject"}
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* Controls Row */}
                <div className="w-full">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        {/* Download section — hidden for FinJournals when the user lacks
                            FinJournal download/versions access (matrix rows 99–100). */}
                        {canDownloadFileArtifact(file, finjournalCaps, tbglCaps) ? (
                            <div className="flex items-center gap-2">
                                <select
                                    value={selectedDownloadType}
                                    onChange={(e) => setSelectedDownloadType(e.target.value)}
                                    className="px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    {availableDownloadTypes.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                                <Button
                                    variant="outline"
                                    onClick={handleDownload}
                                    disabled={
                                        (selectedDownloadType === "finjournal" &&
                                            !isFinJournalGenerated &&
                                            file.status !== "finjournal_generated" &&
                                            file.status !== "completed") ||
                                        (selectedDownloadType === "data-platform" &&
                                            !isDataPlatformGenerated &&
                                            file.status !== "data_platform_generated" &&
                                            file.status !== "completed")
                                    }
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download File
                                </Button>
                            </div>
                        ) : (
                            <div />
                        )}

                        {/* Right-aligned group: status change section followed by the
                            FinJournal Contributors field to its right. */}
                        <div className="flex flex-wrap items-center gap-4">
                            {isFinJournal && (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">
                                        Contributors
                                    </span>
                                    <div className="w-72">
                                        <MultiSelect
                                            options={contributorOptions}
                                            selectedIds={selectedContributorIds}
                                            onChange={handleContributorsChange}
                                            placeholder="Select contributors..."
                                            lockedIds={lockedContributorIds}
                                            emptyMessage="No other contributors available"
                                            disabled={
                                                !canEditContributors ||
                                                isSavingContributors ||
                                                contributorCandidates.loading ||
                                                isArchived
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Status Change Confirmation Modal */}
                <Modal
                    open={isStatusChangeOpen}
                    onOpenChange={(open) => {
                        if (!isChangingStatus) {
                            setIsStatusChangeOpen(open);
                            if (!open) setStatusChangeError(null);
                        }
                    }}
                    title="Change File Status"
                    description={`Are you sure you want to change the status from "${formatStatus(file.status)}" to "${formatStatus(selectedStatus)}"?`}
                >
                    <div className="space-y-4">
                        <div className="rounded-lg border bg-muted/50 p-4">
                            <div className="flex items-center justify-between">
                                <div className="text-sm">
                                    <span className="text-muted-foreground">From:</span>{" "}
                                    <span className="font-medium">{formatStatus(file.status)}</span>
                                </div>
                                <span className="text-muted-foreground">→</span>
                                <div className="text-sm">
                                    <span className="text-muted-foreground">To:</span>{" "}
                                    <span className="font-medium">
                                        {formatStatus(selectedStatus)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {statusChangeError && (
                            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                                {statusChangeError}
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsStatusChangeOpen(false)}
                                disabled={isChangingStatus}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmStatusChange} disabled={isChangingStatus}>
                                {isChangingStatus ? "Changing..." : "Confirm"}
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* Split Line Item Modal — "choose a number of the splits" */}
                <SplitCountModal
                    open={splitTarget !== null}
                    rowNumber={splitTarget?.rowNumber}
                    isSaving={isSplitting}
                    error={splitError}
                    onSubmit={(count) => void handleSplitSubmit(count)}
                    onClose={handleCloseSplitModal}
                />

                {/* Edit Line Item Modal */}
                <Modal
                    open={editModalOpen}
                    onOpenChange={handleCloseEditModal}
                    title="Financial File Line Info"
                    className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                >
                    {editError && (
                        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                            {editError}
                        </div>
                    )}
                    <div className="overflow-y-auto flex-1 -mx-6 px-6">
                        {isLoadingDropdowns ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                                {editFields.map((field) => {
                                    const formValue = editFormData[field.key];
                                    const displayValue =
                                        formValue !== null && formValue !== undefined
                                            ? String(formValue)
                                            : "";
                                    const originalValue = editingLineItem
                                        ? editingLineItem[field.key]
                                        : null;
                                    const hasOriginalValue =
                                        originalValue !== null &&
                                        originalValue !== undefined &&
                                        String(originalValue).trim() !== "";

                                    // Determine if field is disabled. Amount is the
                                    // exception: on a split row it is the value the
                                    // reviewer must edit for the split to add up.
                                    const isDisabled =
                                        field.alwaysReadOnly ||
                                        (field.editableOnSplitRows &&
                                            !editingLineItem?.isSplitChild) ||
                                        (field.readOnlyWhenHasValue && hasOriginalValue);

                                    // Get dropdown options based on source
                                    let dropdownOptions: Array<{
                                        value: string;
                                        label: string;
                                    }> = [];
                                    if (field.dropdownSource?.startsWith("segment")) {
                                        const segType = parseInt(
                                            field.dropdownSource.replace("segment", ""),
                                            10,
                                        );
                                        const allowed = editSegmentAllowedValues[segType];
                                        if (allowed && allowed.length > 0) {
                                            // Segment 4 codes name a property, so the open
                                            // list spells that out. The value — and the
                                            // resting label — stay the bare code.
                                            dropdownOptions = allowed.map((v) => ({
                                                value: v,
                                                label:
                                                    segType === PROPERTY_LINKED_SEGMENT
                                                        ? formatCodeLabel(
                                                              v,
                                                              editSegment4Descriptions[v],
                                                          )
                                                        : v,
                                            }));
                                        } else if (segType !== PROPERTY_SCOPED_SEGMENT) {
                                            dropdownOptions = editSegmentMappings
                                                .filter((s) => s.segment_type === segType)
                                                .map((s) => ({
                                                    value: s.target_segment_value,
                                                    label: s.target_segment_value,
                                                }));
                                        }
                                        // Segment 2 has no fallback at all: its only
                                        // vocabulary is the line's (account, propcode)
                                        // schedule list — there is no segment-2 master
                                        // list, and the deal's mapping targets belong to
                                        // whichever property they were mapped for. With
                                        // no schedules configured the list stays empty,
                                        // which is the honest state to show.
                                    }

                                    const isAccountSearchField =
                                        !isDisabled &&
                                        (field.dropdownSource === "coaCode" ||
                                            field.dropdownSource === "coaName");
                                    // Propcode renders as SearchableSelect only when editable;
                                    // disabled/read-only propcode fields fall through to plain text rendering.
                                    const isPropcodeField =
                                        field.dropdownSource === "propcode" && !isDisabled;
                                    const isDropdown =
                                        field.type === "dropdown" &&
                                        !isDisabled &&
                                        !isAccountSearchField &&
                                        !isPropcodeField;
                                    const fieldError = mergedEditFieldErrors[field.key];

                                    // Build the SearchableOption seed value for account
                                    // fields. Each field seeds from its OWN value so a line
                                    // with a code but no name (or vice versa) still shows
                                    // what's present at rest.
                                    let accountSeedValue: TSearchableOption | null = null;
                                    if (isAccountSearchField) {
                                        if (
                                            field.dropdownSource === "coaCode" &&
                                            editFormData.breAccountNumber
                                        ) {
                                            accountSeedValue = {
                                                value: editFormData.breAccountNumber,
                                                label: editFormData.breAccountNumber,
                                                code: editFormData.breAccountNumber,
                                                name: editFormData.breAccountName ?? "",
                                            };
                                        } else if (
                                            field.dropdownSource === "coaName" &&
                                            editFormData.breAccountName
                                        ) {
                                            accountSeedValue = {
                                                value: editFormData.breAccountName,
                                                label: editFormData.breAccountName,
                                                code: editFormData.breAccountNumber ?? "",
                                                name: editFormData.breAccountName,
                                            };
                                        }
                                    }

                                    // Build the SearchableOption seed for the propcode field.
                                    // Uses the existing propcode as both value and label at rest.
                                    const propcodeSeedValue: TSearchableOption | null =
                                        isPropcodeField && editFormData.propcode
                                            ? {
                                                  value: editFormData.propcode,
                                                  label: editFormData.propcode,
                                              }
                                            : null;

                                    return (
                                        <div key={field.key} className="space-y-1.5">
                                            <Label
                                                htmlFor={`edit-${field.key}`}
                                                className="text-sm font-medium"
                                            >
                                                {field.label}
                                                {field.required && (
                                                    <span className="text-destructive ml-0.5">
                                                        *
                                                    </span>
                                                )}
                                            </Label>
                                            {isAccountSearchField ? (
                                                <SearchableSelect
                                                    id={`edit-${field.key}`}
                                                    value={accountSeedValue}
                                                    searchOnOpen
                                                    onSearch={
                                                        field.dropdownSource === "coaCode"
                                                            ? searchYardiAccountsByCodeForEditor
                                                            : searchYardiAccountsByNameForEditor
                                                    }
                                                    onSelect={handleAccountSelect}
                                                    placeholder={
                                                        field.dropdownSource === "coaCode"
                                                            ? "Search account code…"
                                                            : "Search account name…"
                                                    }
                                                    error={Boolean(fieldError)}
                                                />
                                            ) : isPropcodeField ? (
                                                <SearchableSelect
                                                    id={`edit-${field.key}`}
                                                    value={propcodeSeedValue}
                                                    searchOnOpen
                                                    onSearch={searchMasterPropertyCodesForEditor}
                                                    onSelect={handlePropcodeSelect}
                                                    placeholder="Search propcode…"
                                                    error={Boolean(fieldError)}
                                                />
                                            ) : isDropdown ? (
                                                <SearchableSelect
                                                    id={`edit-${field.key}`}
                                                    value={
                                                        displayValue
                                                            ? {
                                                                  value: displayValue,
                                                                  label: displayValue,
                                                              }
                                                            : null
                                                    }
                                                    searchOnOpen
                                                    onSearch={async (query) => {
                                                        const q = query.trim().toLowerCase();
                                                        return q
                                                            ? dropdownOptions.filter((opt) =>
                                                                  opt.label
                                                                      .toLowerCase()
                                                                      .includes(q),
                                                              )
                                                            : dropdownOptions;
                                                    }}
                                                    onSelect={(opt) =>
                                                        handleFieldChange(
                                                            field.key,
                                                            opt ? opt.value : "",
                                                        )
                                                    }
                                                    placeholder="Search or select..."
                                                    error={Boolean(fieldError)}
                                                />
                                            ) : (
                                                <Input
                                                    id={`edit-${field.key}`}
                                                    type={
                                                        field.type === "dropdown"
                                                            ? "text"
                                                            : field.type
                                                    }
                                                    value={displayValue}
                                                    onChange={(e) =>
                                                        handleFieldChange(field.key, e.target.value)
                                                    }
                                                    disabled={isDisabled}
                                                    aria-invalid={Boolean(fieldError)}
                                                    className={cn(
                                                        "h-9",
                                                        isDisabled &&
                                                            "bg-muted cursor-not-allowed opacity-70",
                                                        fieldError && "border-destructive",
                                                    )}
                                                />
                                            )}
                                            {fieldError ? (
                                                <p
                                                    className="text-xs text-destructive"
                                                    role="alert"
                                                >
                                                    {fieldError}
                                                </p>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                        <Button
                            variant="outline"
                            onClick={() => handleCloseEditModal(false)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleSaveLineItem} disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                    </div>
                </Modal>

                {/* GL Generation Error Modal */}
                <Modal
                    open={glGenerationError !== null}
                    onOpenChange={(open) => !open && setGLGenerationError(null)}
                    title="Cannot Generate GL"
                >
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">{glGenerationError}</p>
                        <div className="flex justify-end">
                            <Button onClick={() => setGLGenerationError(null)}>OK</Button>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    };
};
